import { useBrowserStorage } from '@aspendental/shared-utils/react';
import { Box, IconButton, InputAdornment, Menu, MenuItem, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useState, useRef, RefObject, useEffect } from 'react';
import { BffBrandType, BffFacility, Header } from '@/types/generated';
import { getSearchInputStyles, getDropMenuStyles, menuItemStyles, menuLinkItemStyles, searchWrapStyles, secondaryMenuButtonStyles } from './LocationSearch.styles';
import TAGButton from '@shared-components/components/TAGButton';
import TAGLink from '@shared-components/components/TAGLink';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import TAGTextFieldInput from '@shared-components/components/TAGTextFieldInput';
import { FACILITY_CODE_STORAGE_KEY } from '@shared-components/constants';
import { useAppContext, useFacilityContext } from '@shared-components/context';
import { getBffFacilitiesByAddress, getBffFacilitiesBySearch } from '@shared-components/services';
import { Maybe } from '@shared-components/types';
import { mapSearchClickEvent } from '@utils';
import { getOdpUrl } from '@utils/generateODPLinks';
interface IProps {
  readonly locationBarFindOfficeUrl?: Header['locationBarFindOfficeUrl'];
  readonly isMobile?: boolean;
  readonly locationNavBarRef?: RefObject<HTMLDivElement>;
  readonly searchOpened?: boolean;
  readonly searchFieldTitle?: Maybe<string>;
  readonly onLocationSelected?: () => void;
}
export default function LocationSearch({
  locationBarFindOfficeUrl,
  isMobile,
  locationNavBarRef,
  searchOpened,
  searchFieldTitle,
  onLocationSelected
}: IProps) {
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isMenuOpened, setMenuOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState<(BffFacility | null)[]>([]);
  const router = useRouter();
  const {
    facilityBrand
  } = useFacilityContext();
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const {
    setValue: setFacilityCode
  } = useBrowserStorage({
    key: FACILITY_CODE_STORAGE_KEY,
    storage: config.featureFlags?.useSessionStorageFacilityCode ? globalThis.sessionStorage : globalThis.localStorage
  });
  const url = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
  const fetchAllLocationsOnEmptyResults = config.featureFlags?.fetchAllLocationsOnEmptyResults;
  const useModernSearch = config.featureFlags?.featureList?.includes('useModernFacilitySearch');
  const findFirstFiveElements = (facilitiesList: (BffFacility | null)[] | null | undefined) => {
    const firstFiveElements = facilitiesList && facilitiesList.length > 5 ? facilitiesList.slice(0, 5) : [...(facilitiesList || [])];
    return firstFiveElements;
  };
  const getFacilityHref = (facility: BffFacility) => {
    const facilityHref = getOdpUrl(facility.address, config);
    if (!facilityHref) return '';
    try {
      const parsedHref = new URL(facilityHref, globalThis.location?.origin);
      if (parsedHref.origin === globalThis.location?.origin) {
        return `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`;
      }
    } catch {
      return facilityHref;
    }
    return facilityHref;
  };
  const facilitiesListBySearch = async () => {
    const data = await getBffFacilitiesBySearch({
      brand: facilityBrand.toString() as BffBrandType,
      address: searchValue,
      url,
      fetchAllIfNoResults: fetchAllLocationsOnEmptyResults,
      appName,
      appVersion
    });
    const firstFiveElements = findFirstFiveElements(data?.facilitiesBySearch);
    return firstFiveElements;
  };
  const facilitiesListByAddress = async () => {
    const data = await getBffFacilitiesByAddress(facilityBrand.toString() as BffBrandType, searchValue, url, fetchAllLocationsOnEmptyResults, appName, appVersion);
    const firstFiveElements = findFirstFiveElements(data?.facilitiesByAddress);
    return firstFiveElements;
  };
  const handleSearchSubmit = async () => {
    const trimmedSearchValue = searchValue.trim();
    if (trimmedSearchValue) {
      setLoading(true);
      setMenuOpened(true);
      const facilitiesResults = await (useModernSearch ? facilitiesListBySearch() : facilitiesListByAddress());
      mapSearchClickEvent({
        numResults: facilitiesResults?.length ?? 0
      });
      setFacilities(facilitiesResults);
      setLoading(false);
    } else {
      setMenuOpened(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSearchSubmit();
    }
  };
  const handleSearchButtonClick = () => {
    void handleSearchSubmit();
  };
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.currentTarget.value);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setMenuOpened(false);
  };
  const handleLocationSelected = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, facility: BffFacility) => {
    event.preventDefault();
    const facilityHref = getFacilityHref(facility);
    if (!facility.code || !facilityHref) return;
    setMenuOpened(false);
    setFacilityCode(facility.code);
    onLocationSelected?.();
    globalThis.setTimeout(() => {
      void router.push(facilityHref);
    }, 0);
  };
  const facilityDistanceFormat = (distance?: Maybe<number>) => {
    return distance?.toFixed(1) || '0.0';
  };
  useEffect(() => {
    if (searchOpened && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpened]);
  return <>
			<Box ref={searchWrapRef} sx={searchWrapStyles} data-sentry-element="Box" data-sentry-source-file="LocationSearch.tsx">
				<TAGTextFieldInput fieldPlaceHolder={searchFieldTitle || 'Search by ZIP Code'} onChange={handleSearchInputChange} onKeyDown={handleKeyPress} value={searchValue} inputSx={getSearchInputStyles(isMobile)} inputProps={{
        id: 'location_search_input',
        'data-testid': 'location_search_input',
        ref: searchInputRef,
        title: searchFieldTitle || 'Search by ZIP Code'
      }} endAdornment={<InputAdornment position="end">
							<IconButton onClick={handleSearchButtonClick} data-testid="location-search-icon-button" aria-label="search submit button">
								<Box color="neutrals.secondary" display="flex">
									<TAGSvgIcon icon="SearchFunc" size={24} />
								</Box>
							</IconButton>
						</InputAdornment>} data-sentry-element="TAGTextFieldInput" data-sentry-source-file="LocationSearch.tsx" />
			</Box>
			<Menu id="header_with_location_navigation_search_result_dropdown" disableScrollLock={true} open={isMenuOpened} sx={getDropMenuStyles(isMobile)} transitionDuration={1} anchorEl={isMobile ? locationNavBarRef?.current : searchWrapRef.current} onClose={handleClose} {...isMobile && {
      marginThreshold: 0
    }} data-sentry-element="Menu" data-sentry-source-file="LocationSearch.tsx">
				{/* Loading state */}
				{loading && <Typography padding="1rem">Loading...</Typography>}
				{/* Empty state */}
				{!loading && !facilities.length && <Typography padding="1rem">
						Sorry, no results were found in the immediate area. Please try another search.{' '}
					</Typography>}
				{!loading && !!facilities.length && facilities.map(facility => {
        if (!facility) return null;
        return <MenuItem key={facility.code} sx={menuItemStyles}>
								<TAGLink href={getFacilityHref(facility)} linkSx={menuLinkItemStyles} onClick={event => handleLocationSelected(event, facility)}>
									<Box display="flex" alignItems="center" gap="0.25rem">
										<Typography variant="bodyMediumSemiBold" color="text.primary">
											{facility.name}
										</Typography>
										<Typography variant="bodyMediumBook" color="primary.dark">
											({facility.address?.city}, {facility.address?.stateCode})
										</Typography>
									</Box>
									{<Box data-testid={`facility_distance_${facility.code}`} display="flex" alignItems="center" gap="0.25rem">
											<Typography fontSize="small" color="neutrals.secondary">
												{facilityDistanceFormat(facility.location?.distance)} mi
											</Typography>
											<Box color="primary.light">
												<TAGSvgIcon icon="ArrowRightFunc" size={12} />
											</Box>
										</Box>}
								</TAGLink>
							</MenuItem>;
      })}
				<TAGButton href={locationBarFindOfficeUrl || '/find-a-location'} variant="secondaryDefault" size="M" sx={secondaryMenuButtonStyles} data-sentry-element="TAGButton" data-sentry-source-file="LocationSearch.tsx">
					See All Locations
				</TAGButton>
			</Menu>
		</>;
}